import React, { useState, useMemo, useCallback, memo, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
  ThemeProvider,
  createTheme,
  CssBaseline,
  CircularProgress,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { SpatialRun } from "../types";
import {
  telemetryInstrumentFolder,
  telemetryLogsRootFrom,
} from "src/utils/telemetry-logs-path";
import { useAuth } from "src/contexts/auth";
import { fetchWithToken } from "src/hooks/useFetch";
import {
  CytoQCStatus,
  STATUS_COLORS,
  WELL_BG_COLORS,
} from "src/utils/cyto-qc-grading";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#121212",
      paper: "#1e1e2f",
    },
  },
});

type QCStatus = CytoQCStatus;

interface RunStatsJSON {
  AnalysisID: string;
  AnalysisVersion: string;
  FileVersion: string;
  FlowCellID: string;
  RunID: string;
  RunName: string;
  DemuxStats: {
    NumPolonies: number;
    PercentAssignedReads: number;
    PercentMismatch: number;
    Batches: DemuxBatch[];
  };
  CytoStats: {
    PercentConfluency: number;
    PercentNucleatedCells: number;
    CellCount: number;
    AverageAssignedCountsPerMM2: number;
    Wells: CytoWell[];
  };
}

interface DemuxBatch {
  BatchName: string;
  NumPolonies: number;
  PercentAssignedReads: number;
  PercentMismatch: number;
  Targets: {
    TargetName: string;
    NumPolonies: number;
    PercentMismatch: number;
  }[];
  Wells: {
    WellLocation: string;
    NumPolonies: number;
    PercentAssignedReads: number;
    PercentMismatch: number;
  }[];
  UnassignedSequences: unknown[];
}

interface ControlTarget {
  TargetName: string;
  ControlType: string;
  MeanAssignedCountPerCell: number;
  AssignedCountPerMM2: number;
}

interface CytoWellBatch {
  BatchName: string;
  MeanAssignedCountPerCell: number;
  AssignedCountsPerMM2: number;
  ExtraCellularRatio: number;
  PercentCellularAssigned: number;
  ControlTargets: ControlTarget[];
  AntibodyTargets: unknown[];
}

interface CytoWell {
  WellLocation: string;
  PercentConfluency: number;
  PercentNucleatedCells: number;
  CellCount: number;
  MeanAssignedBarcodingCountPerCell: number;
  AssignedCountsPerMM2: number;
  MedianCellDiameter: number;
  Batches: CytoWellBatch[];
}

interface QCMetricDef {
  key: string;
  label: string;
  threshold: string;
  unit: string;
  getValue: (wellLoc: string, batchFilter: string) => number | null;
  formatValue: (val: number) => string;
  getStatus: (val: number) => QCStatus;
}

const SENTINEL = -999;
const isValid = (v: number) => v !== SENTINEL && !isNaN(v);

const WELL_GRID_ROWS = ["A", "B", "C", "D", "E", "F"];
const WELL_GRID_COLS = ["1", "2"];

const S3_BUCKET = "elembio-prod-usw2-s3-aviti-telemetry-logs-usw2";

const statusIcon = (status: QCStatus, size: number = 16) => {
  switch (status) {
    case "pass":
      return <CheckCircleIcon sx={{ color: "#4caf50", fontSize: size }} />;
    case "fail":
      return <CancelIcon sx={{ color: "#f44336", fontSize: size }} />;
  }
};

const wellColors: Record<QCStatus, string> = WELL_BG_COLORS;

const statusChipColors: Record<QCStatus, string> = STATUS_COLORS;

function getDemuxWellBatchData(
  stats: RunStatsJSON,
  wellLoc: string,
  batchName: string
) {
  const batch = stats.DemuxStats.Batches.find((b) => b.BatchName === batchName);
  if (!batch) return null;
  return batch.Wells.find((w) => w.WellLocation === wellLoc) || null;
}

function getCytoWell(
  stats: RunStatsJSON,
  wellLoc: string
): CytoWell | undefined {
  return stats.CytoStats.Wells.find((w) => w.WellLocation === wellLoc);
}

function getCytoWellBatch(
  stats: RunStatsJSON,
  wellLoc: string,
  batchName: string
): CytoWellBatch | undefined {
  const well = getCytoWell(stats, wellLoc);
  if (!well) return undefined;
  return well.Batches.find((b) => b.BatchName === batchName);
}

function getGAPDHDensity(
  stats: RunStatsJSON,
  wellLoc: string,
  batchFilter: string
): number | null {
  const well = getCytoWell(stats, wellLoc);
  if (!well) return null;

  const batches =
    batchFilter === "All Batches"
      ? well.Batches
      : well.Batches.filter((b) => b.BatchName === batchFilter);

  let totalDensity = 0;
  let count = 0;
  for (const batch of batches) {
    for (const ct of batch.ControlTargets) {
      if (
        ct.TargetName.startsWith("GAPDH") &&
        isValid(ct.AssignedCountPerMM2)
      ) {
        totalDensity += ct.AssignedCountPerMM2;
        count++;
      }
    }
  }
  return count > 0 ? totalDensity / count : null;
}

function getNSBControl(
  stats: RunStatsJSON,
  wellLoc: string,
  batchFilter: string
): number | null {
  const well = getCytoWell(stats, wellLoc);
  if (!well) return null;

  const batches =
    batchFilter === "All Batches"
      ? well.Batches
      : well.Batches.filter((b) => b.BatchName === batchFilter);

  let totalDensity = 0;
  let count = 0;
  for (const batch of batches) {
    for (const ct of batch.ControlTargets) {
      if (
        ct.ControlType === "NegativeControl" &&
        isValid(ct.AssignedCountPerMM2)
      ) {
        totalDensity += ct.AssignedCountPerMM2;
        count++;
      }
    }
  }
  return count > 0 ? totalDensity / count : null;
}

function getAggregatedDemuxMetric(
  stats: RunStatsJSON,
  wellLoc: string,
  batchFilter: string,
  field: "PercentAssignedReads" | "PercentMismatch"
): number | null {
  if (batchFilter !== "All Batches") {
    const data = getDemuxWellBatchData(stats, wellLoc, batchFilter);
    return data ? data[field] : null;
  }
  const batchesWithWell = stats.DemuxStats.Batches.filter((b) =>
    b.Wells.some((w) => w.WellLocation === wellLoc)
  );
  if (batchesWithWell.length === 0) return null;

  let totalPolonies = 0;
  let weightedSum = 0;
  for (const batch of batchesWithWell) {
    const wellData = batch.Wells.find((w) => w.WellLocation === wellLoc);
    if (wellData) {
      totalPolonies += wellData.NumPolonies;
      weightedSum += wellData[field] * wellData.NumPolonies;
    }
  }
  return totalPolonies > 0 ? weightedSum / totalPolonies : null;
}

function getAggregatedCytoMetric(
  stats: RunStatsJSON,
  wellLoc: string,
  batchFilter: string,
  field: "ExtraCellularRatio" | "AssignedCountsPerMM2"
): number | null {
  const well = getCytoWell(stats, wellLoc);
  if (!well) return null;

  if (batchFilter !== "All Batches") {
    const batch = well.Batches.find((b) => b.BatchName === batchFilter);
    if (!batch) return null;
    const val = batch[field];
    return isValid(val) ? val : null;
  }

  const validBatches = well.Batches.filter((b) => isValid(b[field]));
  if (validBatches.length === 0) return null;
  const sum = validBatches.reduce((acc, b) => acc + b[field], 0);
  return field === "AssignedCountsPerMM2" ? sum : sum / validBatches.length;
}

function computeWellStatus(stats: RunStatsJSON, wellLoc: string): QCStatus {
  const metrics = buildMetricDefs(stats, "All Batches");
  for (const metric of metrics) {
    const val = metric.getValue(wellLoc, "All Batches");
    if (val === null) continue;
    if (metric.getStatus(val) === "fail") return "fail";
  }
  return "pass";
}

function buildMetricDefs(
  stats: RunStatsJSON,
  batchFilter: string
): QCMetricDef[] {
  return [
    {
      key: "assignedRate",
      label: "Assigned Rate",
      threshold: "Fail ≤ 65%",
      unit: "%",
      getValue: (wellLoc) =>
        getAggregatedDemuxMetric(
          stats,
          wellLoc,
          batchFilter,
          "PercentAssignedReads"
        ),
      formatValue: (v) => `${Math.round(v)}%`,
      getStatus: (v) => (v > 65 ? "pass" : "fail"),
    },
    {
      key: "mismatchRate",
      label: "Mismatch Rate",
      threshold: "Informational",
      unit: "%",
      getValue: (wellLoc) =>
        getAggregatedDemuxMetric(
          stats,
          wellLoc,
          batchFilter,
          "PercentMismatch"
        ),
      formatValue: (v) => `${Math.round(v)}%`,
      getStatus: () => "pass",
    },
    {
      key: "extracellularRatio",
      label: "Extracellular Ratio",
      threshold: "Fail ≥ 25%",
      unit: "%",
      getValue: (wellLoc) =>
        getAggregatedCytoMetric(
          stats,
          wellLoc,
          batchFilter,
          "ExtraCellularRatio"
        ),
      formatValue: (v) => `${Math.round(v * 100)}%`,
      getStatus: (v) => (v < 0.25 ? "pass" : "fail"),
    },
    {
      key: "cellAssignedPerMM2",
      label: "Cell Assigned per mm²",
      threshold: "Fail ≤ 1M counts/mm²",
      unit: "k/mm²",
      getValue: (wellLoc) =>
        getAggregatedCytoMetric(
          stats,
          wellLoc,
          batchFilter,
          "AssignedCountsPerMM2"
        ),
      formatValue: (v) => `${Math.round(v / 1000)} k/mm²`,
      getStatus: (v) => (v > 1000000 ? "pass" : "fail"),
    },
    {
      key: "confluency",
      label: "Confluency",
      threshold: "Informational",
      unit: "%",
      getValue: (wellLoc) => {
        const well = getCytoWell(stats, wellLoc);
        return well ? well.PercentConfluency : null;
      },
      formatValue: (v) => `${Math.round(v)}%`,
      getStatus: () => "pass",
    },
    {
      key: "percentNucleated",
      label: "Percent Nucleated",
      threshold: "Fail ≤ 90%",
      unit: "%",
      getValue: (wellLoc) => {
        const well = getCytoWell(stats, wellLoc);
        return well ? well.PercentNucleatedCells : null;
      },
      formatValue: (v) => `${Math.round(v)}%`,
      getStatus: (v) => (v > 90 ? "pass" : "fail"),
    },
    {
      key: "gapdhDensity",
      label: "GAPDH Density",
      threshold: "Informational",
      unit: "k/mm²",
      getValue: (wellLoc) => getGAPDHDensity(stats, wellLoc, batchFilter),
      formatValue: (v) => `${Math.round(v / 1000)} k/mm²`,
      getStatus: () => "pass",
    },
    {
      key: "nsbControl",
      label: "NSB Control",
      threshold: "Informational",
      unit: "k/mm²",
      getValue: (wellLoc) => getNSBControl(stats, wellLoc, batchFilter),
      formatValue: (v) => `${Math.round(v / 1000)} k/mm²`,
      getStatus: () => "pass",
    },
  ];
}

const CytoQCDashboard = ({ run }: { run: SpatialRun }) => {
  const [selectedWells, setSelectedWells] = useState<string[]>([]);
  const [batchFilter, setBatchFilter] = useState("All Batches");
  const [showBatchBreakdown, setShowBatchBreakdown] = useState(false);
  const [showGuidance, setShowGuidance] = useState(false);
  const [stats, setStats] = useState<RunStatsJSON | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth();

  const {
    tenantID,
    instrumentID,
    instrumentSerialNumber,
    instrumentName,
    runID,
  } = run || {};

  useEffect(() => {
    if (!runID) return;
    let cancelled = false;

    setIsLoading(true);
    setLoadError(null);
    setStats(null);
    setSelectedWells([]);

    let tID = tenantID;
    if (tID === "") tID = "ten_traefikee";
    const s3Path = `${S3_BUCKET}/${telemetryLogsRootFrom(run)}/${tID}/${telemetryInstrumentFolder(instrumentID, instrumentSerialNumber, instrumentName, run?.model || run?.instrumentModel)}/runs/${runID}/Cytoprofiling/Instrument/RunStats.json`;

    fetchWithToken(getAccessTokenSilently, "/thumbnailBytes", {
      method: "POST",
      body: JSON.stringify({ path: s3Path }),
    })
      .then(async (data) => {
        if (cancelled) return;
        let parsed: RunStatsJSON;
        if (data instanceof Blob) {
          const text = await data.text();
          parsed = JSON.parse(text);
        } else if (typeof data === "string") {
          parsed = JSON.parse(data);
        } else {
          parsed = data as RunStatsJSON;
        }
        setStats(parsed);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load RunStats.json:", err);
        setLoadError(
          err instanceof Error ? err.message : "Failed to load RunStats.json"
        );
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    runID,
    tenantID,
    instrumentID,
    instrumentSerialNumber,
    instrumentName,
    run,
    getAccessTokenSilently,
  ]);

  const wellLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (run?.wells) {
      for (const w of run.wells) {
        map[w.WellLocation] = w.WellLabel || w.WellLocation;
      }
    }
    return map;
  }, [run?.wells]);

  const wellLocations = useMemo(() => {
    if (!stats) return [];
    return stats.CytoStats.Wells.map((w) => w.WellLocation);
  }, [stats]);

  const batchNames = useMemo(() => {
    if (!stats) return [];
    return stats.DemuxStats.Batches.filter(
      (b) => !b.BatchName.startsWith("CP")
    ).map((b) => b.BatchName);
  }, [stats]);

  const wellStatuses = useMemo(() => {
    if (!stats) return {} as Record<string, QCStatus>;
    const map: Record<string, QCStatus> = {};
    for (const loc of wellLocations) {
      map[loc] = computeWellStatus(stats, loc);
    }
    return map;
  }, [wellLocations, stats]);

  const statusCounts = useMemo(() => {
    const counts = { pass: 0, fail: 0 };
    for (const loc of wellLocations) {
      counts[wellStatuses[loc]]++;
    }
    return counts;
  }, [wellStatuses, wellLocations]);

  const avgConfluency = stats?.CytoStats.PercentConfluency ?? 0;
  const totalCellCount = stats?.CytoStats.CellCount ?? 0;
  const avgCountPerMM2 = stats?.CytoStats.AverageAssignedCountsPerMM2 ?? 0;

  const metrics = useMemo(
    () => (stats ? buildMetricDefs(stats, batchFilter) : []),
    [batchFilter, stats]
  );

  const handleWellClick = useCallback(
    (wellLoc: string, event: React.MouseEvent) => {
      if (event.shiftKey) {
        setSelectedWells((prev) =>
          prev.includes(wellLoc)
            ? prev.filter((w) => w !== wellLoc)
            : [...prev, wellLoc]
        );
      } else {
        setSelectedWells((prev) =>
          prev.length === 1 && prev[0] === wellLoc ? [] : [wellLoc]
        );
      }
    },
    []
  );

  const headerCellSx = {
    color: "#9e9e9e",
    fontSize: "0.7rem",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    borderBottom: "1px solid #333",
    py: 1.5,
    px: 2,
  };

  if (isLoading) {
    return (
      <ThemeProvider theme={darkTheme}>
        <Box
          sx={{
            bgcolor: "#121212",
            borderRadius: 2,
            p: 3,
            minHeight: "calc(100vh - 180px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: "#7c4dff" }} />
          <Typography variant="body2" sx={{ color: "#999" }}>
            Loading Run Review data...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (loadError || !stats) {
    return (
      <ThemeProvider theme={darkTheme}>
        <Box
          sx={{
            bgcolor: "#121212",
            borderRadius: 2,
            p: 3,
            minHeight: "calc(100vh - 180px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CancelIcon sx={{ color: "#f44336", fontSize: 40 }} />
          <Typography
            variant="subtitle1"
            sx={{ color: "#fff", fontWeight: 600 }}
          >
            Failed to load Run Review
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#999", maxWidth: 400, textAlign: "center" }}
          >
            {loadError || "RunStats.json was not found for this run."}
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <Box
        sx={{
          color: "#e0e0e0",
          bgcolor: "#121212",
          borderRadius: 2,
          p: 3,
          overflow: "hidden",
          minHeight: "calc(100vh - 180px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{ color: "#fff", fontWeight: 600, mb: 2 }}
          >
            Run Review
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 6,
              alignItems: "flex-start",
              flexWrap: "wrap",
              background: "rgba(255,255,255,0.03)",
              borderRadius: 2,
              p: 2.5,
              border: "1px solid #2a2a2a",
            }}
          >
            <HeaderStat
              label="Run Name"
              value={stats?.RunName || run?.runName || "—"}
            />
            <HeaderStat
              label="Cartridge"
              value={run?.panelPartNumber || "Teton Cartridge"}
            />
            <HeaderStat
              label="Total Cell Count"
              value={totalCellCount.toLocaleString()}
            />
            <HeaderStat
              label="Avg. Count/mm²"
              value={`${Math.round(avgCountPerMM2 / 1000).toLocaleString()}`}
            />
            <HeaderStat
              label="Avg. Well Confluency"
              value={`${avgConfluency.toFixed(1)}%`}
            />
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#777", fontSize: "0.7rem" }}
              >
                Overall Well Status
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  mt: 0.5,
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {statusIcon("pass", 14)}
                  <Typography
                    variant="body2"
                    sx={{ color: "#fff", fontWeight: 500 }}
                  >
                    {statusCounts.pass}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {statusIcon("fail", 14)}
                  <Typography
                    variant="body2"
                    sx={{ color: "#fff", fontWeight: 500 }}
                  >
                    {statusCounts.fail}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Well Performance */}
        <Box sx={{ mb: 3, flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ color: "#fff", fontWeight: 600 }}
            >
              Well Performance
            </Typography>
            <IconButton size="small" sx={{ color: "#777" }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ color: "#999" }}>
                Wells (
              </Typography>
              {selectedWells.map((w, i) => (
                <Chip
                  key={w}
                  label={w}
                  size="small"
                  onDelete={() =>
                    setSelectedWells((prev) => prev.filter((x) => x !== w))
                  }
                  sx={{
                    bgcolor: statusChipColors[wellStatuses[w] || "pass"],
                    color: "#fff",
                    height: 22,
                    fontSize: "0.75rem",
                    "& .MuiChip-deleteIcon": {
                      color: "rgba(255,255,255,0.7)",
                      fontSize: 14,
                    },
                  }}
                />
              ))}
              <Typography variant="body2" sx={{ color: "#999" }}>
                )
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 3, flex: 1 }}>
            {/* Well Map */}
            <Box
              sx={{
                background: "rgba(255,255,255,0.03)",
                borderRadius: 2,
                p: 2.5,
                border: "1px solid #2a2a2a",
                minWidth: 420,
              }}
            >
              <Typography variant="subtitle2" sx={{ color: "#fff", mb: 1 }}>
                Well Map
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#888", display: "block", mb: 2 }}
              >
                Shift+select to choose multiple wells to compare statistics.
              </Typography>

              <Box sx={{ display: "flex", gap: 0.5, mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#999",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mr: 2,
                  }}
                >
                  {statusIcon("pass", 12)} {statusCounts.pass} Pass
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#999",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {statusIcon("fail", 12)} {statusCounts.fail} Fail
                </Typography>
              </Box>

              <Box sx={{ display: "inline-block" }}>
                {WELL_GRID_COLS.map((col) => (
                  <Box key={col} sx={{ display: "flex", gap: 0.5, mb: 0.5 }}>
                    {WELL_GRID_ROWS.map((row) => {
                      const loc = `${row}${col}`;
                      const status = wellStatuses[loc] || "pass";
                      const isSelected = selectedWells.includes(loc);

                      return (
                        <Box
                          key={loc}
                          onClick={(e) => handleWellClick(loc, e)}
                          sx={{
                            width: 52,
                            height: 40,
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#fff",
                            bgcolor: wellColors[status],
                            border: isSelected
                              ? "2px solid #fff"
                              : "2px solid transparent",
                            opacity: isSelected ? 1 : 0.75,
                            transition: "all 0.15s ease",
                            "&:hover": { opacity: 1, transform: "scale(1.05)" },
                          }}
                        >
                          {loc}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Well Statistics Table or Empty State */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {selectedWells.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      background: "rgba(255,255,255,0.05)",
                      borderRadius: 2,
                      border: "1px solid #333",
                      p: 5,
                      textAlign: "center",
                      maxWidth: 500,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        bgcolor: "#7c4dff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <ChatBubbleOutlineIcon
                        sx={{ color: "#fff", fontSize: 24 }}
                      />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "#fff", fontWeight: 600, mb: 1 }}
                    >
                      Select wells for quality control
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#999", mb: 3 }}>
                      In the well map, select one or more wells to view
                      statistics.
                    </Typography>
                    <Box sx={{ textAlign: "left", display: "inline-block" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        {statusIcon("pass", 16)}
                        <Typography variant="caption" sx={{ color: "#ccc" }}>
                          Pass: All disposition metrics within acceptable range.
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {statusIcon("fail", 16)}
                        <Typography variant="caption" sx={{ color: "#ccc" }}>
                          Fail: One or more disposition metrics outside
                          acceptable range. Data may be compromised.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: "#fff" }}>
                      Well Statistics
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Select
                        value={batchFilter}
                        onChange={(e) => setBatchFilter(e.target.value)}
                        size="small"
                        sx={{
                          color: "#ccc",
                          fontSize: "0.8rem",
                          ".MuiOutlinedInput-notchedOutline": {
                            borderColor: "#444",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#666",
                          },
                          ".MuiSvgIcon-root": { color: "#999" },
                          minWidth: 130,
                        }}
                      >
                        <MenuItem value="All Batches">All Batches</MenuItem>
                        {batchNames.map((b) => (
                          <MenuItem key={b} value={b}>
                            {b}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showBatchBreakdown}
                            onChange={(e) =>
                              setShowBatchBreakdown(e.target.checked)
                            }
                            size="small"
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "#7c4dff",
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                { bgcolor: "#7c4dff" },
                            }}
                          />
                        }
                        label={
                          <Typography variant="caption" sx={{ color: "#999" }}>
                            Batch Breakdown
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showGuidance}
                            onChange={(e) => setShowGuidance(e.target.checked)}
                            size="small"
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "#7c4dff",
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                { bgcolor: "#7c4dff" },
                            }}
                          />
                        }
                        label={
                          <Typography variant="caption" sx={{ color: "#999" }}>
                            Guidance
                          </Typography>
                        }
                      />
                    </Box>
                  </Box>

                  <TableContainer
                    sx={{
                      background: "rgba(255,255,255,0.03)",
                      borderRadius: 2,
                      border: "1px solid #2a2a2a",
                      overflowX: "auto",
                    }}
                  >
                    <Table size="small" sx={{ tableLayout: "auto" }}>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              ...headerCellSx,
                              minWidth: 200,
                              position: "sticky",
                              left: 0,
                              zIndex: 2,
                              bgcolor: "#1e1e2f",
                            }}
                          >
                            Metric & Threshold
                          </TableCell>
                          {selectedWells.map((wellLoc, i) => (
                            <TableCell
                              key={wellLoc}
                              sx={{ ...headerCellSx, whiteSpace: "nowrap" }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    bgcolor:
                                      statusChipColors[
                                        wellStatuses[wellLoc] || "pass"
                                      ],
                                  }}
                                />
                                {wellLoc}: {wellLabelMap[wellLoc] || wellLoc}
                              </Box>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {metrics.map((metric) => (
                          <TableRow
                            key={metric.key}
                            sx={{
                              "&:hover": { bgcolor: "rgba(255,255,255,0.02)" },
                            }}
                          >
                            <TableCell
                              sx={{
                                borderBottom: "1px solid #2a2a2a",
                                py: 1.5,
                                px: 2,
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                                bgcolor: "#1e1e2f",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Tooltip title={metric.threshold}>
                                  <InfoOutlinedIcon
                                    sx={{
                                      color: "#666",
                                      fontSize: 14,
                                      cursor: "help",
                                    }}
                                  />
                                </Tooltip>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#e0e0e0",
                                      fontWeight: 500,
                                      fontSize: "0.8rem",
                                    }}
                                  >
                                    {metric.label}
                                  </Typography>
                                  {showGuidance && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#777",
                                        fontSize: "0.65rem",
                                        display: "block",
                                        whiteSpace: "pre-line",
                                      }}
                                    >
                                      {metric.threshold}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            {selectedWells.map((wellLoc) => {
                              const val = metric.getValue(wellLoc, batchFilter);
                              const status =
                                val !== null ? metric.getStatus(val) : "pass";
                              const isInformational =
                                metric.threshold === "Informational";
                              return (
                                <TableCell
                                  key={wellLoc}
                                  sx={{
                                    borderBottom: "1px solid #2a2a2a",
                                    py: 1.5,
                                    px: 2,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    {!isInformational && statusIcon(status, 14)}
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "#fff",
                                        fontWeight: 500,
                                        fontSize: "0.85rem",
                                      }}
                                    >
                                      {val !== null
                                        ? metric.formatValue(val)
                                        : "—"}
                                    </Typography>
                                    <BarChartIcon
                                      sx={{ color: "#555", fontSize: 16 }}
                                    />
                                  </Box>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {showBatchBreakdown && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#999", mb: 1, display: "block" }}
                      >
                        Batch Breakdown — Per-batch statistics for selected
                        wells
                      </Typography>
                      <TableContainer
                        sx={{
                          background: "rgba(255,255,255,0.03)",
                          borderRadius: 2,
                          border: "1px solid #2a2a2a",
                          maxHeight: 400,
                        }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  ...headerCellSx,
                                  bgcolor: "#1a1a2e",
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 3,
                                }}
                              >
                                Batch
                              </TableCell>
                              <TableCell
                                sx={{ ...headerCellSx, bgcolor: "#1a1a2e" }}
                              >
                                Well
                              </TableCell>
                              <TableCell
                                sx={{ ...headerCellSx, bgcolor: "#1a1a2e" }}
                                align="center"
                              >
                                Assigned %
                              </TableCell>
                              <TableCell
                                sx={{ ...headerCellSx, bgcolor: "#1a1a2e" }}
                                align="center"
                              >
                                Mismatch %
                              </TableCell>
                              <TableCell
                                sx={{ ...headerCellSx, bgcolor: "#1a1a2e" }}
                                align="center"
                              >
                                Extracell. Ratio
                              </TableCell>
                              <TableCell
                                sx={{ ...headerCellSx, bgcolor: "#1a1a2e" }}
                                align="center"
                              >
                                Assigned/mm²
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {batchNames.map((bn) =>
                              selectedWells.map((wellLoc) => {
                                const demux = stats
                                  ? getDemuxWellBatchData(stats, wellLoc, bn)
                                  : null;
                                const cyto = stats
                                  ? getCytoWellBatch(stats, wellLoc, bn)
                                  : undefined;
                                return (
                                  <TableRow key={`${bn}-${wellLoc}`}>
                                    <TableCell
                                      sx={{
                                        borderBottom: "1px solid #2a2a2a",
                                        color: "#ccc",
                                        fontSize: "0.8rem",
                                        position: "sticky",
                                        left: 0,
                                        bgcolor: "#1e1e2f",
                                        zIndex: 1,
                                      }}
                                    >
                                      {bn}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        borderBottom: "1px solid #2a2a2a",
                                        color: "#aaa",
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      {wellLoc}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      sx={{
                                        borderBottom: "1px solid #2a2a2a",
                                        color: "#fff",
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      {demux
                                        ? `${demux.PercentAssignedReads.toFixed(
                                            1
                                          )}%`
                                        : "—"}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      sx={{
                                        borderBottom: "1px solid #2a2a2a",
                                        color: "#fff",
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      {demux
                                        ? `${demux.PercentMismatch.toFixed(1)}%`
                                        : "—"}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      sx={{
                                        borderBottom: "1px solid #2a2a2a",
                                        color: "#fff",
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      {cyto && isValid(cyto.ExtraCellularRatio)
                                        ? `${(
                                            cyto.ExtraCellularRatio * 100
                                          ).toFixed(1)}%`
                                        : "—"}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      sx={{
                                        borderBottom: "1px solid #2a2a2a",
                                        color: "#fff",
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      {cyto &&
                                      isValid(cyto.AssignedCountsPerMM2)
                                        ? `${Math.round(
                                            cyto.AssignedCountsPerMM2 / 1000
                                          )} k/mm²`
                                        : "—"}
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* Well Overview */}
        {selectedWells.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ color: "#fff", fontWeight: 600, mb: 2 }}
            >
              Well Overview
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {selectedWells.map((wellLoc, idx) => {
                const cytoWell = stats
                  ? getCytoWell(stats, wellLoc)
                  : undefined;
                const status = wellStatuses[wellLoc] || "pass";

                return (
                  <Box
                    key={wellLoc}
                    sx={{
                      background: "rgba(255,255,255,0.03)",
                      borderRadius: 2,
                      p: 2.5,
                      border: "1px solid #2a2a2a",
                      minWidth: 220,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography variant="caption" sx={{ color: "#777" }}>
                          Well
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#fff", fontWeight: 600 }}
                        >
                          {wellLoc}: {wellLabelMap[wellLoc] || wellLoc}
                        </Typography>
                      </Box>
                      {statusIcon(status, 20)}
                    </Box>
                    <Box sx={{ display: "flex", gap: 4, mt: 1 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: "#777" }}>
                          Cell Count
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#fff", fontWeight: 500 }}
                        >
                          {cytoWell?.CellCount?.toLocaleString() || "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: "#777" }}>
                          Cell Diameter
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#fff", fontWeight: 500 }}
                        >
                          {cytoWell
                            ? `${Math.round(cytoWell.MedianCellDiameter)} μm`
                            : "—"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 1,
                        cursor: "pointer",
                        color:
                          statusChipColors[wellStatuses[wellLoc] || "pass"],
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Explore in flow cell
                      </Typography>
                      <ArrowForwardIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

const HeaderStat = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant="caption" sx={{ color: "#777", fontSize: "0.7rem" }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ color: "#fff", fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);

export default memo(CytoQCDashboard);
